/**
 * TanStack Query hooks bound to the API client.
 *
 * Mock-data fallback: if the API returns an empty cases list (the placeholder
 * Lambda does), we surface a small set of mock cases so the UI is visually
 * meaningful during development. Once a real backend lands, drop the fallback
 * by deleting the `?? MOCK_CASES` branch in `useCases`.
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type Approval,
  type ApprovalDecision,
  type ApprovalListFilter,
  type AuthorizedApplication,
  type Case,
  type CaseTimelineEvent,
  type EvidenceItem,
  type Gap,
  type Health,
  type Marking,
  type Organization,
  type Person,
  type AuditEntry,
  type IdentityProvider,
  type IdentityProviderInput,
  type PrincipalAssignment,
  type PrincipalRef,
  type Role,
  type RoleInput,
  type RolePolicy,
  type RoleSet,
  type SourceRef,
  type SurveillanceClip,
  type Warrant,
} from "./api";
import { MOCK_ENTITIES, MOCK_RELATIONSHIPS } from "./entities-data";
import type { EntityRelationship, IntelligenceEntity } from "./entities-ontology";
import { detectGaps } from "./gap-detection";

// -- Demo "now" anchor + relative-date helpers ------------------------------
//
// The CalECPA 72h window and the "within 7 days / 14 days" gap rules must fire
// deterministically regardless of when the demo runs, so the mock fixtures are
// authored RELATIVE to a fixed `NOW` captured at module load. `isoHours`/
// `isoDays` produce ISO timestamps offset from NOW (negative = past).
const NOW = Date.now();
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
/** ISO timestamp `n` hours from NOW (negative => in the past). */
const isoHours = (n: number): string => new Date(NOW + n * HOUR_MS).toISOString();
/** ISO timestamp `n` days from NOW (negative => in the past). */
const isoDays = (n: number): string => new Date(NOW + n * DAY_MS).toISOString();
/** ISO date (YYYY-MM-DD) `n` days from NOW. */
const isoDateDays = (n: number): string => isoDays(n).slice(0, 10);

const MOCK_CASES: Case[] = [
  {
    id: "case-2026-00945",
    caseNumber: "DR 26-00945",
    title: "Armed robbery — Tropicana & Mountain Vista",
    type: "Robbery (211 PC)",
    status: "open",
    agency: "Sample PD",
    leadDetective: "Det. Castaneda #4471",
    evidenceCount: 47,
    personCount: 12,
    gapCount: 3,
    flagged: true,
    createdAt: "2026-02-13T01:53:00Z",
    updatedAt: "2026-05-25T18:22:00Z",
  },
  {
    id: "case-2026-00821",
    caseNumber: "DR 26-00821",
    title: "Burglary investigation — Westside auto-parts",
    type: "Commercial burglary (459 PC)",
    // Legacy "in_review" was migrated to "suspended" backend-side (BCM enum).
    status: "suspended",
    agency: "Sample PD",
    leadDetective: "Det. Reyes #2204",
    evidenceCount: 23,
    personCount: 6,
    gapCount: 1,
    flagged: false,
    createdAt: "2026-01-30T11:04:00Z",
    updatedAt: "2026-05-24T09:11:00Z",
  },
  {
    id: "case-2026-00733",
    caseNumber: "DR 26-00733",
    title: "Domestic dispute — extended monitoring",
    type: "Battery / domestic (243(e)(1) PC)",
    status: "open",
    agency: "Sample PD",
    leadDetective: "Det. Park #5512",
    evidenceCount: 18,
    personCount: 4,
    gapCount: 5,
    flagged: true,
    createdAt: "2026-01-08T22:31:00Z",
    updatedAt: "2026-05-23T14:48:00Z",
  },
  {
    id: "case-2025-12044",
    caseNumber: "DR 25-12044",
    title: "Auto theft — recovered with prints",
    type: "Vehicle theft (10851 VC)",
    status: "closed",
    agency: "Sample PD",
    leadDetective: "Det. Holm #3398",
    evidenceCount: 31,
    personCount: 8,
    gapCount: 0,
    flagged: false,
    createdAt: "2025-12-19T16:55:00Z",
    updatedAt: "2026-04-02T12:30:00Z",
  },
  {
    id: "case-2025-11876",
    caseNumber: "DR 25-11876",
    title: "Identity fraud — multi-victim",
    type: "Identity theft (530.5 PC)",
    status: "suspended",
    agency: "Sample PD",
    leadDetective: "Det. Okafor #4017",
    evidenceCount: 89,
    personCount: 27,
    gapCount: 7,
    flagged: false,
    createdAt: "2025-11-11T03:18:00Z",
    updatedAt: "2026-05-19T16:02:00Z",
  },
];

export function useCases() {
  return useQuery({
    queryKey: ["cases"],
    queryFn: async () => {
      const { items: cases } = await api.listCases();
      return cases;
    },
    // No placeholderData — surfacing MOCK_CASES (5 fake rows) before the API
    // resolves causes a visible flash when the real result is [] or differs.
    // Consumers should branch on isPending to render their loading state.
  });
}

export function useCase(caseId: string | undefined) {
  return useQuery({
    queryKey: ["cases", caseId],
    enabled: Boolean(caseId),
    queryFn: async () => {
      const { items: cases } = await api.listCases();
      return cases.find((c) => c.id === caseId) ?? null;
    },
    placeholderData: MOCK_CASES.find((c) => c.id === caseId) ?? null,
  });
}

// Case activity feed (CaseOverview BC, GET /api/cases/:id/timeline). Mock
// fallback mirrors the prior hardcoded Overview-tab rows.
const MOCK_TIMELINE: CaseTimelineEvent[] = [
  {
    id: "tl-006",
    caseId: "case-2026-00945",
    occurredAt: "2026-02-13T01:53:00Z",
    summary: "Case opened — armed robbery (211 PC), Tropicana & Mountain Vista",
    source: "manual",
    reportNumber: "DR 26-00945",
    officer: "Officer R. Park #5512",
    actions:
      "Initial response to 211 silent-alarm. Scene secured; victim (clerk) Diana Pham contacted; one armed suspect fled eastbound in a gray sedan. BWC activated on arrival.",
    personIds: ["p-003", "p-004"],
    evidenceIds: ["ev-001"],
    warrantIds: [],
    autoFields: ["reportNumber", "officer", "personIds", "evidenceIds"],
  },
  {
    id: "tl-007",
    caseId: "case-2026-00945",
    occurredAt: "2026-02-13T03:14:00Z",
    summary: "BWC uploaded; scene canvass identifies three camera sources",
    source: "ingest",
    confidence: 1,
    reportNumber: "DR 26-00945",
    officer: "Officer R. Park #5512",
    actions:
      "Canvassed the block. Identified store interior CCTV, parking-lot camera (Eastside Properties), and Mountain Vista city camera #114. Photo log attached as Exhibit B.",
    personIds: ["p-004"],
    evidenceIds: ["ev-001"],
    warrantIds: [],
    autoFields: ["reportNumber", "officer"],
  },
  {
    id: "tl-008",
    caseId: "case-2026-00945",
    occurredAt: "2026-02-14T15:32:00Z",
    summary: "Store CCTV collected and booked; latent prints lifted from register",
    source: "ingest",
    confidence: 1,
    reportNumber: "DR 26-00945-S1",
    officer: "Det. Castaneda #4471",
    actions:
      "Pulled interior + parking-lot CCTV from Eastside Properties (booked). CSI Holt lifted 3 latent prints from the register surface. Scene collection log notes a 9mm casing and a buccal swab recovered near the door.",
    personIds: ["p-001", "p-007"],
    evidenceIds: ["ev-002", "ev-003", "ev-007"],
    warrantIds: [],
    autoFields: ["reportNumber", "officer", "evidenceIds"],
  },
  {
    id: "tl-009",
    caseId: "case-2026-00945",
    occurredAt: "2026-02-15T11:08:00Z",
    summary: "Witness interview — store clerk J. Martinez",
    source: "ingest",
    confidence: 0.97,
    reportNumber: "DR 26-00945-S1",
    officer: "Det. Park #5512",
    actions:
      "Recorded interview of clerk Jessica Martinez; identified suspect by build and tattoo. A second witness, Tomás Ruiz, is named in the canvass notes but has not been interviewed.",
    personIds: ["p-002", "p-007"],
    evidenceIds: ["ev-004"],
    warrantIds: [],
    autoFields: ["reportNumber", "officer", "personIds", "evidenceIds"],
  },
  {
    id: "tl-010",
    caseId: "case-2026-00945",
    occurredAt: "2026-04-22T16:30:00Z",
    summary: "Device search warrant SW-22409 served; Cellebrite extract ingested",
    source: "ingest",
    confidence: 1,
    reportNumber: "DR 26-00945-S2",
    officer: "Det. Park #5512",
    actions:
      "Served SW-22409 on the seized iPhone; Cellebrite UFED extract returned 312 entities including CDR-linked numbers and a Google account.",
    personIds: ["p-001"],
    evidenceIds: ["ev-005"],
    warrantIds: ["w-001"],
    autoFields: ["reportNumber", "officer", "evidenceIds", "warrantIds"],
  },
  {
    id: "tl-011",
    caseId: "case-2026-00945",
    occurredAt: "2026-05-09T09:00:00Z",
    summary: "Subpoenaed Wells Fargo records ingested; financial entities extracted",
    source: "ingest",
    confidence: 0.93,
    reportNumber: "DR 26-00945-S3",
    officer: "Det. Reyes #2204",
    actions:
      "Bank records returned; surfaced a suspected shell company (Eastside Holdings LLC) and three cash deposits over $4k. See attachment referenced as Exhibit D (transfer trace) — file not yet linked to the case.",
    personIds: ["p-001", "p-006"],
    evidenceIds: ["ev-006"],
    warrantIds: [],
    autoFields: ["reportNumber", "officer", "evidenceIds"],
  },
  {
    id: "tl-012",
    caseId: "case-2026-00945",
    occurredAt: isoHours(-30),
    summary: "Entity extraction surfaced a new pertinent number from the CDR set",
    source: "ai",
    confidence: 0.74,
    reportNumber: "DR 26-00945-S2",
    officer: "Det. Castaneda #4471",
    actions:
      "CDR analysis flagged +1 (702) 555-0613 (unsubscribed/burner) in frequent contact with the suspect handset around the offense window. No carrier warrant has issued for this number.",
    personIds: ["p-005"],
    evidenceIds: ["ev-005"],
    warrantIds: [],
    autoFields: ["reportNumber", "officer", "evidenceIds"],
  },
];

export function useCaseTimeline(caseId: string | undefined) {
  return useQuery<CaseTimelineEvent[]>({
    queryKey: ["timeline", caseId],
    enabled: Boolean(caseId),
    queryFn: async () => {
      try {
        const { timeline } = await api.listCaseTimeline(caseId!);
        return timeline.length ? timeline : MOCK_TIMELINE;
      } catch {
        return MOCK_TIMELINE;
      }
    },
    placeholderData: MOCK_TIMELINE,
  });
}

// -- Entities — link-analysis intelligence graph ----------------------------
//
// Wired to the EntityResolutionAndLinkAnalysis BC (GET /api/entity-graph).
// Mock-data fallback (same convention as useCases): if the endpoint is empty
// (placeholder Lambda) or errors, fall back to the representative client-side
// dataset so the workspace stays visually meaningful. Drop the `?? MOCK_*`
// branches once the real backend is deployed + seeded.

export function useEntities() {
  return useQuery({
    queryKey: ["entities"],
    queryFn: async (): Promise<IntelligenceEntity[]> => {
      try {
        const { entities } = await api.getEntityGraph();
        return entities.length ? (entities as IntelligenceEntity[]) : MOCK_ENTITIES;
      } catch {
        return MOCK_ENTITIES;
      }
    },
  });
}

export function useEntityGraph() {
  return useQuery({
    queryKey: ["entity-graph"],
    queryFn: async (): Promise<{
      entities: IntelligenceEntity[];
      relationships: EntityRelationship[];
    }> => {
      try {
        const graph = await api.getEntityGraph();
        return graph.entities.length
          ? {
              entities: graph.entities as IntelligenceEntity[],
              relationships: graph.relationships as EntityRelationship[],
            }
          : { entities: MOCK_ENTITIES, relationships: MOCK_RELATIONSHIPS };
      } catch {
        return { entities: MOCK_ENTITIES, relationships: MOCK_RELATIONSHIPS };
      }
    },
  });
}

export function useHealth() {
  return useQuery<Health>({
    queryKey: ["health"],
    queryFn: () => api.health(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

// Mock fixtures retained only as placeholderData for the first cold-start
// render. Real /api/cases/:caseId/{evidence|persons|warrants|surveillance}
// endpoints are live; queryFn calls them.

const MOCK_EVIDENCE: Record<string, EvidenceItem[]> = {
  "case-2026-00945": [
    {
      id: "ev-001",
      caseId: "case-2026-00945",
      kind: "video",
      title: "BWC — Officer Park — initial response",
      source: "Axon Evidence sync",
      uploadedAt: "2026-02-13T03:14:00Z",
      uploadedBy: "Det. Castaneda #4471",
      sizeBytes: 1_842_300_000,
      entityCount: 14,
      hitl: "approved",
      cocSealed: true,
      itemNumber: "E-01",
      reportNumber: "DR 26-00945",
      location: "Booked — Digital Evidence Vault (Axon)",
      forensicAnalysisNeeded: false,
      warrantRequired: false,
      notes: "Initial-response body-worn camera, ~30 min.",
      sourceRefs: [{ evidenceId: "ev-001", label: "BWC metadata — Officer Park", locator: "device serial" }],
      autoFields: ["reportNumber", "location"],
    },
    {
      id: "ev-002",
      caseId: "case-2026-00945",
      kind: "video",
      title: "Tropicana convenience store — interior CCTV",
      source: "Manual upload",
      uploadedAt: "2026-02-14T15:32:00Z",
      uploadedBy: "Det. Castaneda #4471",
      sizeBytes: 2_410_998_000,
      entityCount: 22,
      hitl: "approved",
      cocSealed: true,
      itemNumber: "E-02",
      reportNumber: "DR 26-00945-S1",
      location: "Booked — Digital Evidence Vault",
      forensicAnalysisNeeded: false,
      warrantRequired: false,
      notes: "Interior CCTV, 00:38:14. Suspect visible at register.",
      sourceRefs: [{ evidenceId: "ev-002", label: "Eastside Properties CCTV export", locator: "00:14:22" }],
      autoFields: ["reportNumber", "location"],
    },
    {
      // Marked for forensic analysis but NO lab request, uploaded > 14 days ago
      // => fires gap C-d (lab request not initiated).
      id: "ev-003",
      caseId: "case-2026-00945",
      kind: "image",
      title: "Latent print — register surface (3 lifts)",
      source: "Manual upload",
      uploadedAt: isoDays(-26),
      uploadedBy: "CSI Holt #220",
      sizeBytes: 18_400_000,
      entityCount: 2,
      hitl: "not_required",
      cocSealed: true,
      itemNumber: "E-04",
      reportNumber: "DR 26-00945-S1",
      location: "Booked — Property & Evidence, latent shelf",
      forensicAnalysisNeeded: true,
      analysisType: "Latent print (AFIS comparison)",
      warrantRequired: false,
      // sentToLab intentionally undefined — no lab request initiated.
      notes: "3 lifts from register surface; comparison not yet requested.",
      sourceRefs: [{ evidenceId: "ev-003", label: "CSI latent-lift log", locator: "lifts 1–3" }],
      autoFields: ["reportNumber", "location", "analysisType"],
    },
    {
      id: "ev-004",
      caseId: "case-2026-00945",
      kind: "interview_transcript",
      title: "Witness interview — store clerk J. Martinez",
      source: "AWS Transcribe",
      uploadedAt: "2026-02-15T11:08:00Z",
      uploadedBy: "Det. Park #5512",
      sizeBytes: 1_240_000,
      entityCount: 18,
      hitl: "pending",
      cocSealed: false,
      itemNumber: "E-05",
      reportNumber: "DR 26-00945-S1",
      location: "Case file — interview folder",
      forensicAnalysisNeeded: false,
      warrantRequired: false,
      notes: "Recorded interview; clerk identifies suspect by build + tattoo.",
      sourceRefs: [{ evidenceId: "ev-004", label: "Witness interview transcript", locator: "p.3" }],
      autoFields: ["reportNumber"],
    },
    {
      id: "ev-005",
      caseId: "case-2026-00945",
      kind: "device_extract",
      title: "Cellebrite extract — suspect phone (warrant SW-22409)",
      source: "Cellebrite UFED",
      uploadedAt: "2026-04-22T16:30:00Z",
      uploadedBy: "Det. Park #5512",
      sizeBytes: 12_800_000_000,
      entityCount: 312,
      hitl: "pending",
      cocSealed: true,
      itemNumber: "E-06",
      reportNumber: "DR 26-00945-S2",
      location: "Booked — Digital Evidence Vault (sealed)",
      forensicAnalysisNeeded: false,
      warrantRequired: true,
      notes: "Full logical + file-system extract under SW-22409.",
      sourceRefs: [{ evidenceId: "ev-005", label: "Cellebrite extract — accounts", locator: "p.3" }],
      autoFields: ["reportNumber", "location", "warrantRequired"],
    },
    {
      id: "ev-006",
      caseId: "case-2026-00945",
      kind: "document",
      title: "Subpoenaed bank records — Wells Fargo",
      source: "Manual upload",
      uploadedAt: "2026-05-09T09:00:00Z",
      uploadedBy: "Det. Reyes #2204",
      sizeBytes: 8_400_000,
      entityCount: 47,
      hitl: "approved",
      cocSealed: true,
      itemNumber: "E-07",
      reportNumber: "DR 26-00945-S3",
      location: "Case file — financial records",
      forensicAnalysisNeeded: false,
      warrantRequired: false,
      notes: "Checking + business accounts; 3 cash deposits over $4k.",
      sourceRefs: [{ evidenceId: "ev-006", label: "Wells Fargo records", locator: "p.2" }],
      autoFields: ["reportNumber"],
    },
    {
      // Scene collection log: a 9mm casing is described as collected, but it was
      // never assigned an evidence item number => fires gap B-a (unbooked
      // evidence). The buccal swab WAS booked (E-08) as a control contrast.
      id: "ev-007",
      caseId: "case-2026-00945",
      kind: "scene_note",
      title: "Scene collection log — 9mm casing + buccal swab",
      source: "Field collection",
      uploadedAt: "2026-02-14T16:05:00Z",
      uploadedBy: "CSI Holt #220",
      sizeBytes: 96_000,
      entityCount: 0,
      hitl: "not_required",
      cocSealed: false,
      // itemNumber intentionally undefined — the casing was collected but never
      // booked into Property & Evidence.
      reportNumber: "DR 26-00945-S1",
      location: "Scene — near entry door",
      forensicAnalysisNeeded: true,
      analysisType: "Ballistics (NIBIN entry)",
      warrantRequired: false,
      notes:
        "Log states a 9mm shell casing was collected at the door; no item number assigned. Buccal swab collected (booked E-08).",
      sourceRefs: [{ evidenceId: "ev-007", label: "Scene collection log", locator: "line 4" }],
      autoFields: ["reportNumber", "location", "analysisType"],
    },
    {
      // Control: forensic analysis needed AND lab request initiated, partly
      // through the lab lifecycle — exercises the Sent to Lab / Lab Used /
      // Analyzed / Returned columns and does NOT fire C-d.
      id: "ev-008",
      caseId: "case-2026-00945",
      kind: "scene_note",
      title: "Buccal swab — scene (DNA reference)",
      source: "Field collection",
      uploadedAt: "2026-02-14T16:10:00Z",
      uploadedBy: "CSI Holt #220",
      sizeBytes: 72_000,
      entityCount: 0,
      hitl: "not_required",
      cocSealed: true,
      itemNumber: "E-08",
      reportNumber: "DR 26-00945-S1",
      location: "Booked — Property & Evidence, refrigerated",
      forensicAnalysisNeeded: true,
      analysisType: "DNA (STR profile)",
      warrantRequired: false,
      sentToLab: "2026-02-20T09:00:00Z",
      labUsed: "Sample County Regional Crime Lab",
      analyzedAt: "2026-03-18T00:00:00Z",
      // returnedAt intentionally still pending — shows an in-flight lab item.
      notes: "Submitted for STR profile; results pending return.",
      sourceRefs: [{ evidenceId: "ev-008", label: "Lab submission form", locator: "RFL-0042" }],
      autoFields: ["reportNumber", "location", "analysisType"],
    },
  ],
};

const MOCK_PERSONS: Record<string, Person[]> = {
  "case-2026-00945": [
    {
      id: "p-001",
      caseId: "case-2026-00945",
      fullName: "Marcus L. Tatum",
      role: "suspect",
      ageBracket: "28–32",
      linkedEvidence: 14,
      verified: true,
      verifiedBy: "Det. Castaneda #4471",
      confidence: 0.96,
      lastSeen: "2026-02-13T01:53:00Z",
      reportsMentionedIn: ["DR 26-00945", "DR 26-00945-S1", "DR 26-00945-S2"],
      interviewed: false,
      notes: "Primary suspect; identified by clerk and on interior CCTV. Not yet in custody.",
      sourceRefs: [{ evidenceId: "ev-004", label: "Witness interview transcript", locator: "p.3" }],
      autoFields: ["reportsMentionedIn"],
    },
    {
      // Control: interviewed witness with a linked interview doc => no gap.
      id: "p-002",
      caseId: "case-2026-00945",
      fullName: "Jessica E. Martinez",
      role: "witness",
      ageBracket: "20–24",
      linkedEvidence: 4,
      verified: true,
      verifiedBy: "Det. Park #5512",
      confidence: 1.0,
      lastSeen: "2026-02-15T11:08:00Z",
      reportsMentionedIn: ["DR 26-00945-S1"],
      interviewed: true,
      interviewDate: "2026-02-15",
      interviewEvidenceId: "ev-004",
      notes: "Store clerk; recorded interview on file (E-05).",
      sourceRefs: [{ evidenceId: "ev-004", label: "Witness interview transcript", locator: "p.1" }],
      autoFields: ["reportsMentionedIn", "interviewEvidenceId"],
    },
    {
      id: "p-003",
      caseId: "case-2026-00945",
      fullName: "Diana K. Pham",
      role: "victim",
      ageBracket: "35–39",
      linkedEvidence: 6,
      verified: true,
      verifiedBy: "Det. Castaneda #4471",
      confidence: 1.0,
      reportsMentionedIn: ["DR 26-00945"],
      interviewed: true,
      interviewDate: "2026-02-13",
      notes: "Victim (clerk on duty); on-scene statement taken by responding officer.",
      sourceRefs: [{ evidenceId: "ev-001", label: "Incident report narrative", locator: "§2" }],
      autoFields: ["reportsMentionedIn"],
    },
    {
      id: "p-004",
      caseId: "case-2026-00945",
      fullName: "Officer R. Park",
      role: "officer",
      badge: "#5512",
      agency: "Sample PD",
      linkedEvidence: 8,
      verified: true,
      confidence: 1.0,
      reportsMentionedIn: ["DR 26-00945", "DR 26-00945-S1", "DR 26-00945-S2"],
      interviewed: false,
      notes: "Responding officer; authored initial and supplemental reports.",
      sourceRefs: [{ evidenceId: "ev-001", label: "BWC metadata — Officer Park" }],
      autoFields: ["reportsMentionedIn"],
    },
    {
      id: "p-005",
      caseId: "case-2026-00945",
      fullName: "Unidentified male — passenger seat",
      role: "personOfInterest",
      ageBracket: "25–40 (visual)",
      linkedEvidence: 3,
      verified: false,
      confidence: 0.62,
      lastSeen: "2026-02-13T01:48:00Z",
      reportsMentionedIn: ["DR 26-00945-S2"],
      interviewed: false,
      notes: "Seen in parking-lot CCTV; possibly tied to burner +1 (702) 555-0613. Low-confidence ID.",
      sourceRefs: [{ evidenceId: "ev-002", label: "Parking-lot CCTV", locator: "frame 01:48:22" }],
      autoFields: ["reportsMentionedIn"],
    },
    {
      id: "p-006",
      caseId: "case-2026-00945",
      fullName: "Kyle D. Tatum",
      role: "associate",
      ageBracket: "26–30",
      linkedEvidence: 2,
      verified: false,
      confidence: 0.81,
      reportsMentionedIn: ["DR 26-00945-S2", "DR 26-00945-S3"],
      interviewed: false,
      notes: "Brother of suspect; registered owner of the gray Charger; registered agent of Eastside Holdings LLC.",
      sourceRefs: [{ evidenceId: "ev-005", label: "Cellebrite report — contacts", locator: "p.12" }],
      autoFields: ["reportsMentionedIn"],
    },
    {
      // Witness named in the canvass notes but NEVER interviewed and with no
      // interview/statement doc => fires gap A-a (uninterviewed witness).
      id: "p-007",
      caseId: "case-2026-00945",
      fullName: "Tomás Ruiz",
      role: "witness",
      ageBracket: "40–44",
      linkedEvidence: 1,
      verified: false,
      confidence: 0.7,
      reportsMentionedIn: ["DR 26-00945-S2"],
      interviewed: false,
      // interviewEvidenceId intentionally undefined — no interview exists.
      notes: "Bystander named in canvass notes; reportedly saw the suspect vehicle leave. No statement taken.",
      sourceRefs: [{ evidenceId: "ev-002", label: "Store CCTV canvass notes", locator: "frame 1142" }],
      autoFields: ["reportsMentionedIn"],
    },
  ],
};

const MOCK_WARRANTS: Record<string, Warrant[]> = {
  "case-2026-00945": [
    {
      // Control: fully returned + filed + discovery provided => Completed (green).
      id: "w-001",
      caseId: "case-2026-00945",
      warrantNumber: "SW-22409",
      type: "device_search",
      status: "returned",
      target: "iPhone 13 Pro (IMEI 35-XXXXX-XXXXX-3) — suspect phone",
      draftedAt: "2026-04-18T10:00:00Z",
      draftedBy: "Det. Castaneda #4471",
      signedBy: "Hon. R. Salazar",
      court: "Sample County Superior Court",
      servedAt: "2026-04-22T16:00:00Z",
      returnFiledAt: "2026-05-02T14:00:00Z",
      calecpaPredicate: "Probable cause — robbery (211 PC) + identification by witness",
      expiresAt: "2026-05-18T23:59:00Z",
      author: "Det. Castaneda #4471",
      dateSignedAt: "2026-04-20",
      courthouse: "Sample County Superior Court — Dept. 12",
      judge: "Hon. R. Salazar",
      warrantObject: "Logical + file-system extraction of seized iPhone 13 Pro",
      miscNotes: "Executed via Cellebrite UFED; return filed within 10 days.",
      serviceProviderTrackingId: "—",
      allDataReceived: "yes",
      dataFiled: true,
      swReturn: true,
      swCopyAttached: true,
      wcpdReportNumber: "DR 26-00945-S2",
      searchWarrantNumber: "SW-22409",
      discoveryProvidedToDa: true,
      pertinentPhone: "+17025550147",
      boardStatus: "completed",
      sourceRefs: [{ evidenceId: "ev-005", label: "Cellebrite extract — device info" }],
      autoFields: ["author", "dateSignedAt", "courthouse", "judge", "warrantObject", "searchWarrantNumber"],
    },
    {
      // Partial data received, no return yet while served/signed => Action Required.
      // Covers the suspect handset (+17025550147) as the pertinent phone.
      id: "w-002",
      caseId: "case-2026-00945",
      warrantNumber: "SW-22517",
      type: "telecom_records",
      status: "served",
      target: "T-Mobile subscriber records + CSLI 2026-02-13 00:00–04:00 for +1 (702) 555-0147",
      draftedAt: "2026-05-10T11:30:00Z",
      draftedBy: "Det. Castaneda #4471",
      signedBy: "Hon. M. Okereke",
      court: "Sample County Superior Court",
      servedAt: "2026-05-12T09:00:00Z",
      calecpaPredicate: "1546.1(c)(1) — geolocation evidence of robbery suspect",
      expiresAt: "2026-06-09T23:59:00Z",
      author: "Det. Castaneda #4471",
      dateSignedAt: "2026-05-11",
      courthouse: "Sample County Superior Court — Dept. 9",
      judge: "Hon. M. Okereke",
      warrantObject: "Subscriber records + cell-site location information (CSLI)",
      miscNotes: "Subscriber records received; CSLI tranche still outstanding.",
      serviceProvider: "t_mobile",
      serviceProviderTrackingId: "TMO-LE-884213",
      dataExpirationDate: "2026-06-09",
      calecpaNotifications: "na",
      allDataReceived: "partial",
      dataFiled: false,
      swReturn: false,
      swCopyAttached: true,
      wcpdReportNumber: "DR 26-00945-S2",
      searchWarrantNumber: "SW-22517",
      discoveryProvidedToDa: false,
      pertinentPhone: "+17025550147",
      boardStatus: "action_required",
      sourceRefs: [{ evidenceId: "ev-005", label: "Subscriber records SW-22517" }],
      autoFields: ["author", "dateSignedAt", "courthouse", "judge", "warrantObject", "serviceProvider", "searchWarrantNumber"],
    },
    {
      // Draft awaiting detective review => No Action Required (compliance cols blank).
      id: "w-003",
      caseId: "case-2026-00945",
      warrantNumber: "(draft)",
      type: "google_data",
      status: "pending_hitl",
      target: "Google account marcus.tatum81@gmail.com — content + connection logs",
      draftedAt: "2026-05-25T16:00:00Z",
      draftedBy: "IRIS Assistant draft → Det. Castaneda #4471",
      calecpaPredicate: "1546.1(c)(1) — pending detective review",
      author: "Det. Castaneda #4471",
      warrantObject: "Account content, connection logs, and subscriber info for the Google account",
      miscNotes: "Structured draft prepared by IRIS; awaiting detective review before submission.",
      serviceProvider: "google",
      dataFiled: false,
      swReturn: false,
      swCopyAttached: false,
      discoveryProvidedToDa: false,
      boardStatus: "no_action_required",
      sourceRefs: [{ evidenceId: "ev-005", label: "Cellebrite extract — accounts", locator: "p.3" }],
      autoFields: ["warrantObject", "serviceProvider"],
    },
    {
      // Social-media warrant on Meta with a CalECPA DON expiration WITHIN 72h
      // => fires gap C-a (CalECPA expiration) with a live countdown;
      // boardStatus URGENT (red).
      id: "w-004",
      caseId: "case-2026-00945",
      warrantNumber: "SW-22640",
      type: "social_media",
      status: "served",
      target: "Meta (Instagram/Facebook) records for handle @big_m_lv — content + connection logs",
      draftedAt: "2026-05-01T10:00:00Z",
      draftedBy: "Det. Castaneda #4471",
      signedBy: "Hon. A. Whitfield",
      court: "Sample County Superior Court",
      servedAt: "2026-05-04T09:00:00Z",
      calecpaPredicate: "1546.1(c)(1) — electronic communication content of robbery suspect",
      author: "Det. Castaneda #4471",
      dateSignedAt: "2026-05-02",
      courthouse: "Sample County Superior Court — Dept. 4",
      judge: "Hon. A. Whitfield",
      warrantObject: "Stored content, connection logs, and subscriber info for the Meta account",
      miscNotes:
        "CalECPA delayed-notice (DON) period expires within 72h — detective must notify the target or seek an extension.",
      serviceProvider: "meta",
      serviceProviderTrackingId: "META-LE-2026-55190",
      donStartDate: isoDateDays(-87),
      donExpirationDate: isoHours(+48),
      dataExpirationDate: isoDateDays(+60),
      calecpaNotifications: "yes",
      notificationMethod: "mail",
      allDataReceived: "yes",
      dataFiled: false,
      swReturn: false,
      swCopyAttached: true,
      wcpdReportNumber: "DR 26-00945-S4",
      searchWarrantNumber: "SW-22640",
      discoveryProvidedToDa: false,
      boardStatus: "action_required_urgent",
      sourceRefs: [{ evidenceId: "ev-005", label: "Cellebrite extract — accounts", locator: "p.4" }],
      autoFields: ["author", "dateSignedAt", "courthouse", "judge", "warrantObject", "serviceProvider", "donStartDate", "donExpirationDate", "searchWarrantNumber"],
    },
  ],
};

const MOCK_SURVEILLANCE: Record<string, SurveillanceClip[]> = {
  "case-2026-00945": [
    {
      // Control: fully collected, booked, downloaded, reviewed => no gap.
      id: "sv-001",
      caseId: "case-2026-00945",
      title: "Tropicana convenience store — interior CCTV",
      source: "Vendor: Eastside Properties",
      capturedAt: "2026-02-13T01:48:00Z",
      durationSeconds: 38 * 60 + 14,
      transcriptStatus: "available",
      transcriptConfidence: 0.88,
      preservationConfirmed: true,
      locationName: "Tropicana Convenience — interior",
      address: "5120 Tropicana Ave, Sample City",
      collectedBy: "Det. Castaneda #4471",
      dateCollected: "2026-02-14",
      periodCovered: "2026-02-13 00:00–04:00",
      booked: true,
      reportNumber: "DR 26-00945-S1",
      reviewed: true,
      downloadConfirmed: true,
      sourceRefs: [{ evidenceId: "ev-002", label: "Eastside Properties CCTV export" }],
      autoFields: ["locationName", "address", "periodCovered", "reportNumber"],
    },
    {
      // Control: collected + booked + downloaded; review still pending.
      id: "sv-002",
      caseId: "case-2026-00945",
      title: "Parking lot — north-facing camera",
      source: "Vendor: Eastside Properties",
      capturedAt: "2026-02-13T01:42:00Z",
      durationSeconds: 22 * 60 + 6,
      transcriptStatus: "available",
      transcriptConfidence: 0.79,
      preservationConfirmed: true,
      locationName: "Tropicana Convenience — north parking lot",
      address: "5120 Tropicana Ave, Sample City",
      collectedBy: "Det. Castaneda #4471",
      dateCollected: "2026-02-14",
      periodCovered: "2026-02-13 01:00–02:30",
      booked: true,
      reportNumber: "DR 26-00945-S1",
      reviewed: false,
      downloadConfirmed: true,
      sourceRefs: [{ evidenceId: "ev-002", label: "Parking-lot CCTV", locator: "frame 01:42:10" }],
      autoFields: ["locationName", "address", "periodCovered", "reportNumber"],
    },
    {
      // Camera identified in the canvass but NEVER collected: no dateCollected,
      // no collection report, download not confirmed => fires gap A-b
      // (unprocessed surveillance source) AND C-b (overwrite risk).
      id: "sv-003",
      caseId: "case-2026-00945",
      title: "Mountain Vista intersection — city camera #114",
      source: "City Public Works",
      capturedAt: "2026-02-13T01:50:00Z",
      durationSeconds: 12 * 60 + 41,
      transcriptStatus: "pending",
      preservationConfirmed: false,
      locationName: "Mountain Vista & Tropicana — city camera #114",
      address: "Tropicana Ave & Mountain Vista St (intersection)",
      // collectedBy / dateCollected / reportNumber intentionally undefined.
      periodCovered: "2026-02-13 (offense window — not yet pulled)",
      booked: false,
      reviewed: false,
      downloadConfirmed: false,
      sourceRefs: [{ evidenceId: "ev-001", label: "Scene canvass notes (BWC)", locator: "camera #114" }],
      autoFields: ["locationName", "address"],
    },
  ],
};

function caseScoped<T>(map: Record<string, T[]>, caseId: string | undefined): T[] {
  if (!caseId) return [];
  return map[caseId] ?? [];
}

export function useEvidence(caseId: string | undefined) {
  return useQuery({
    queryKey: ["evidence", caseId],
    enabled: Boolean(caseId),
    queryFn: async () => {
      const { evidence } = await api.listEvidence(caseId!);
      return evidence;
    },
    placeholderData: caseScoped(MOCK_EVIDENCE, caseId),
  });
}

export function usePersons(caseId: string | undefined) {
  return useQuery({
    queryKey: ["persons", caseId],
    enabled: Boolean(caseId),
    queryFn: async () => {
      const { persons } = await api.listPersons(caseId!);
      return persons;
    },
    placeholderData: caseScoped(MOCK_PERSONS, caseId),
  });
}

export function useWarrants(caseId: string | undefined) {
  return useQuery({
    queryKey: ["warrants", caseId],
    enabled: Boolean(caseId),
    queryFn: async () => {
      const { warrants } = await api.listWarrants(caseId!);
      return warrants;
    },
    placeholderData: caseScoped(MOCK_WARRANTS, caseId),
  });
}

/**
 * Pages produced by the extraction pipeline for an evidence file.
 *
 * Polls while no pages are present yet (extraction not finished), then drops
 * to a long refetch interval once content lands. The poll lets the reader
 * route refresh from a "Loading…" state to the populated reader without the
 * user reloading. 10s is gentle but quick enough that a Bedrock extraction
 * landing in ~30s is visible within one poll cycle.
 */
export function useExtractedPages(evidenceId: string | undefined) {
  return useQuery({
    queryKey: ["extracted", evidenceId],
    enabled: Boolean(evidenceId),
    queryFn: () => api.getExtractedPages(evidenceId!),
    refetchInterval: (query) => {
      const data = query.state.data as { pages?: unknown[] } | undefined;
      return data && data.pages && data.pages.length > 0 ? 60_000 : 10_000;
    },
  });
}

export function useSurveillance(caseId: string | undefined) {
  return useQuery({
    queryKey: ["surveillance", caseId],
    enabled: Boolean(caseId),
    queryFn: async () => {
      const { surveillance } = await api.listSurveillance(caseId!);
      return surveillance;
    },
    placeholderData: caseScoped(MOCK_SURVEILLANCE, caseId),
  });
}

// -- CAP 4: Gap detection ----------------------------------------------------
//
// `useCaseGaps` is the single read-side join for the gap-detection layer: it
// pulls every case-scoped query (evidence / persons / warrants / surveillance
// / timeline) plus the extracted phone entities, then runs the pure
// `detectGaps` rules (lib/gap-detection.ts) over them. Returns a memoized
// Gap[] so the Active-Flags tab badge and the Urgent banner can read
// `useCaseGaps(caseId).length` directly. The detective decides; IronCase only
// surfaces — no gap auto-acts.

/**
 * Build the pertinent-phone list ({phone, sourceRef}) for a case from the
 * link-analysis graph (MOCK_ENTITIES kind==="phone"), filtered to the case's
 * reference number, so gap C-c (carrier data retention) can check each
 * extracted number against the warrants.
 */
function phoneEntitiesForCase(
  entities: IntelligenceEntity[],
  caseNumber: string | undefined,
): { phone: string; sourceRef: SourceRef }[] {
  return entities
    .filter((e) => e.kind === "phone" && (!caseNumber || e.originCaseReference === caseNumber))
    .map((e) => {
      const cite = e.attributes[0]?.sourceCitation ?? "Link-analysis graph";
      return {
        phone: e.canonicalIdentifier,
        sourceRef: { evidenceId: e.id, label: `Phone entity ${e.displayName}`, locator: cite } as SourceRef,
      };
    });
}

/**
 * Joined inputs + detected gaps for a case. Reads the same query caches the
 * tabs use (so it stays in sync) and runs `detectGaps` client-side. Memoized
 * on the underlying arrays.
 */
export function useCaseGaps(caseId: string | undefined): Gap[] {
  const caseQuery = useCase(caseId);
  const evidence = useEvidence(caseId).data ?? [];
  const persons = usePersons(caseId).data ?? [];
  const warrants = useWarrants(caseId).data ?? [];
  const surveillance = useSurveillance(caseId).data ?? [];
  const timeline = useCaseTimeline(caseId).data ?? [];
  const entities = useEntities().data ?? MOCK_ENTITIES;

  const caseRecord = caseQuery.data ?? null;

  return useMemo<Gap[]>(() => {
    if (!caseRecord) return [];
    const phoneEntities = phoneEntitiesForCase(entities, caseRecord.caseNumber);
    // Bound to a variable (not an inline literal) so the call stays compatible
    // whether or not the gap-agent's GapInputs lists every field — extra
    // properties on a variable are not excess-property-checked.
    const inputs = {
      caseRecord,
      evidence,
      persons,
      warrants,
      surveillance,
      timeline,
      phoneEntities,
    };
    return detectGaps(inputs);
  }, [caseRecord, evidence, persons, warrants, surveillance, timeline, entities]);
}

// -- Multipass — Identity & Access settings ---------------------------------
//
// No /api/multipass/* endpoints exist yet. Each hook's queryFn returns mock
// data directly. When the IAM backend lands, swap the `return MOCK_X` line
// for `return (await api.listX()).<key>` and delete the MOCK_X constant —
// the call sites in routes/settings.tsx don't change.

const MOCK_ROLE_SETS: RoleSet[] = [
  { id: "rs-marketplace", name: "Marketplace Installation Role Set", context: "Marketplace installation", organizationId: null },
  { id: "rs-ontology", name: "Ontology Defaults", context: "Ontology", organizationId: null },
  { id: "rs-project", name: "Project defaults", context: "Project", organizationId: null },
  { id: "rs-oauth2", name: "Third-party application OAuth 2.0 client defaults", context: "OAuth2 client", organizationId: null },
];

const MOCK_ORGANIZATIONS: Organization[] = [
  { id: "org-ironcase", name: "ironcase", description: "" },
];

const MOCK_MARKINGS: Marking[] = [];

const MOCK_AUTHORIZED_APPLICATIONS: AuthorizedApplication[] = [
  { id: "app-frontend", name: "ironcase-frontend", managingAgencyId: "org-ironcase" },
];

export function useRoleSets() {
  return useQuery<RoleSet[]>({
    queryKey: ["multipass", "role-sets"],
    queryFn: async () => {
      try {
        const { roleSets } = await api.listRoleSets();
        return roleSets.length ? roleSets : MOCK_ROLE_SETS;
      } catch {
        return MOCK_ROLE_SETS;
      }
    },
    placeholderData: MOCK_ROLE_SETS,
  });
}

export function useOrganizations() {
  return useQuery<Organization[]>({
    queryKey: ["multipass", "organizations"],
    queryFn: async () => {
      try {
        const { organizations } = await api.listOrganizations();
        return organizations.length ? organizations : MOCK_ORGANIZATIONS;
      } catch {
        return MOCK_ORGANIZATIONS;
      }
    },
    placeholderData: MOCK_ORGANIZATIONS,
  });
}

export function useMarkings() {
  return useQuery<Marking[]>({
    queryKey: ["multipass", "markings"],
    queryFn: async () => {
      try {
        const { markings } = await api.listMarkings();
        return markings.length ? markings : MOCK_MARKINGS;
      } catch {
        return MOCK_MARKINGS;
      }
    },
    placeholderData: MOCK_MARKINGS,
  });
}

export function useAuthorizedApplications() {
  return useQuery<AuthorizedApplication[]>({
    queryKey: ["multipass", "authorized-applications"],
    queryFn: async () => {
      try {
        const { applications } = await api.listAuthorizedApplications();
        return applications.length ? applications : MOCK_AUTHORIZED_APPLICATIONS;
      } catch {
        return MOCK_AUTHORIZED_APPLICATIONS;
      }
    },
    placeholderData: MOCK_AUTHORIZED_APPLICATIONS,
  });
}

// -- RBAC admin — Roles & Cedar policies (ADR-0022 Phase 3) -----------------
//
// Real backend (12-01): /api/admin/rbac/* roles + role-policy CRUD. No mock
// fallback — these are admin surfaces, so a query error (e.g. 403 not
// authorized, 503 policy engine not configured) MUST surface to the operator
// rather than being masked by demo data. Mutations invalidate the roles list
// (and the specific role / policy keys) so the UI re-fetches persisted state.
// Every mutation is audit-logged server-side per 12-01 (fail-closed); no
// client-side audit is needed.

const RBAC_ROLES_KEY = ["rbac", "roles"] as const;

export function useRoles() {
  return useQuery<Role[]>({
    queryKey: RBAC_ROLES_KEY,
    queryFn: async () => {
      const { roles } = await api.rbacAdmin.listRoles();
      return roles;
    },
  });
}

export function useRole(roleId: string | undefined) {
  return useQuery<Role | null>({
    queryKey: ["rbac", "roles", roleId],
    enabled: Boolean(roleId),
    queryFn: async () => {
      const { role } = await api.rbacAdmin.getRole(roleId!);
      return role;
    },
  });
}

export function useRolePolicy(roleId: string | undefined) {
  return useQuery<RolePolicy | null>({
    queryKey: ["rbac", "roles", roleId, "policy"],
    enabled: Boolean(roleId),
    // A role may legitimately have no policy yet (404). Treat that as null
    // rather than an error so the editor renders an empty/new-policy state.
    retry: false,
    queryFn: async () => {
      const { policy } = await api.rbacAdmin.getRolePolicy(roleId!);
      return policy;
    },
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoleInput) => api.rbacAdmin.createRole(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RBAC_ROLES_KEY });
    },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, body }: { roleId: string; body: RoleInput }) =>
      api.rbacAdmin.updateRole(roleId, body),
    onSuccess: (_data, { roleId }) => {
      qc.invalidateQueries({ queryKey: RBAC_ROLES_KEY });
      qc.invalidateQueries({ queryKey: ["rbac", "roles", roleId] });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => api.rbacAdmin.deleteRole(roleId),
    onSuccess: (_data, roleId) => {
      qc.invalidateQueries({ queryKey: RBAC_ROLES_KEY });
      qc.invalidateQueries({ queryKey: ["rbac", "roles", roleId] });
    },
  });
}

export function useUpdateRolePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, cedarText }: { roleId: string; cedarText: string }) =>
      api.rbacAdmin.updateRolePolicy(roleId, cedarText),
    onSuccess: (_data, { roleId }) => {
      qc.invalidateQueries({ queryKey: ["rbac", "roles", roleId, "policy"] });
      // Permission changes ride along with the policy; re-fetch the role too.
      qc.invalidateQueries({ queryKey: ["rbac", "roles", roleId] });
      qc.invalidateQueries({ queryKey: RBAC_ROLES_KEY });
    },
  });
}

// -- RBAC admin — Principal assignments (ADMIN-03/05/06) --------------------
//
// Assign / list / remove the users & groups granted a role. Same no-mock-
// fallback contract as the roles hooks above: a 403 (not platform_admin) or
// 503 (policy engine not configured) MUST surface to the operator. Each
// mutation invalidates both the role's principal list AND the roles-list key
// — assignment counts on the role cards change when principals are
// added/removed. Every mutation is audit-logged server-side (fail-closed) per
// 12-01, so no client-side audit is needed.

/** Cache key for a role's assigned principals. */
function rolePrincipalsKey(roleId: string) {
  return ["rbac", "roles", roleId, "principals"] as const;
}

export function useRolePrincipals(roleId: string | undefined) {
  return useQuery<PrincipalAssignment[]>({
    queryKey: ["rbac", "roles", roleId, "principals"],
    enabled: Boolean(roleId),
    queryFn: async () => {
      const { principals } = await api.rbacAdmin.listRolePrincipals(roleId!);
      return principals;
    },
  });
}

/**
 * Assignable principals — the directory of users + groups an admin can grant
 * to a role. Sourced from GET /api/admin/rbac/principals (12-04), the real
 * Cognito-backed directory — no longer a stub that re-reads the role's own
 * principals. The directory is agency-wide (not role-scoped), so the cache key
 * is global `["rbac","principals"]` and one fetch is shared across every role;
 * the panel excludes already-assigned principals client-side.
 *
 * No mock fallback (admin-surface contract): a 403 (not platform_admin) or 503
 * (directory unavailable — e.g. the API role lacks cognito-idp:ListUsers /
 * ListGroups) MUST surface so the panel can show a Callout and fall back to
 * manual add-by-id rather than masking the condition with fake candidates.
 */
/** The agency's audit trail, newest first. */
export function useAudit(limit = 100) {
  return useQuery<AuditEntry[]>({
    queryKey: ["audit", limit],
    // A 403 means this reader may not read the audit log, which is a fact about
    // them and not a flake.
    retry: false,
    queryFn: async () => (await api.listAudit(limit)).items,
  });
}

const IDP_KEY = ["identity-providers"] as const;

/** Register a federated identity provider. */
export function useCreateIdentityProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IdentityProviderInput) => api.createIdentityProvider(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: IDP_KEY }),
  });
}

/** Remove one by name. */
export function useDeleteIdentityProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.deleteIdentityProvider(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: IDP_KEY }),
  });
}

/** Federated identity providers configured for this deployment. */
export function useIdentityProviders() {
  return useQuery<IdentityProvider[]>({
    queryKey: IDP_KEY,
    retry: false,
    queryFn: async () => (await api.listIdentityProviders()).providers,
  });
}

/**
 * Every principal of a kind, independent of any role.
 *
 * `useAssignablePrincipals` takes a roleId and is gated on it, because it exists
 * to populate a role's picker. The endpoint behind it
 * (`GET /api/admin/rbac/principals`) does not use the role at all — so the Users
 * and Groups pages, which have no role in hand, needed the same call without the
 * gate rather than a fake role id passed in to satisfy a parameter.
 */
export function usePrincipals(kind: "user" | "group") {
  return useQuery<PrincipalAssignment[]>({
    queryKey: ["rbac", "principals", "all", kind],
    // A 403 here is meaningful operator signal — the caller is not an admin —
    // not a transient flake to retry into a rate limit.
    retry: false,
    queryFn: async () => {
      const { principals } = await api.rbacAdmin.listAssignablePrincipals("", kind);
      return principals;
    },
  });
}

export function useAssignablePrincipals(
  roleId: string | undefined,
  kind?: "user" | "group",
) {
  return useQuery<PrincipalAssignment[]>({
    queryKey: ["rbac", "principals", kind ?? "all"],
    enabled: Boolean(roleId),
    // A 403/503 here is meaningful operator signal, not a transient flake.
    retry: false,
    queryFn: async () => {
      const { principals } = await api.rbacAdmin.listAssignablePrincipals(roleId!, kind);
      return principals;
    },
  });
}

export function useAssignPrincipal(roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (principal: PrincipalRef) => api.rbacAdmin.assignPrincipal(roleId, principal),
    onSuccess: () => {
      // Re-fetch the role's assigned set (drives which directory candidates the
      // picker excludes) and the roles list (assignment counts on the cards
      // change). The directory itself (["rbac","principals",…]) is the full
      // agency pool and is unchanged by an assignment, so it is not invalidated.
      qc.invalidateQueries({ queryKey: rolePrincipalsKey(roleId) });
      qc.invalidateQueries({ queryKey: RBAC_ROLES_KEY });
    },
  });
}

export function useUnassignPrincipal(roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (principal: PrincipalRef) => api.rbacAdmin.unassignPrincipal(roleId, principal),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rolePrincipalsKey(roleId) });
      qc.invalidateQueries({ queryKey: RBAC_ROLES_KEY });
    },
  });
}

// -- P6: Human-in-the-loop approvals (HumanDecisioning) ---------------------
//
// Real backend (/api/v1/approvals). NO mock fallback — this is the HITL
// worklist, so a query error (403 not authorized, 503 engine down) MUST surface
// to the reviewer rather than being masked by demo data. The decide mutation
// invalidates the whole "approvals" key prefix so BOTH the queue list and the
// open detail re-fetch the persisted post-decision state. Every read + the
// decide are audit-logged server-side (fail-closed) — no client-side audit.

const APPROVALS_KEY = ["approvals"] as const;

export function useApprovals(filter?: ApprovalListFilter) {
  return useQuery<Approval[]>({
    // Filter rides in the key so server-filtered fetches cache independently;
    // call with no filter (the queue does) to fetch the full set and filter
    // client-side for instant, flash-free status/risk switching.
    queryKey: [...APPROVALS_KEY, filter?.status ?? "all", filter?.riskLevel ?? "all"],
    queryFn: async () => {
      const { items } = await api.approvals.list(filter);
      return items;
    },
  });
}

export function useApproval(id: string | undefined) {
  return useQuery<Approval | null>({
    queryKey: [...APPROVALS_KEY, "detail", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { approval } = await api.approvals.get(id!);
      return approval;
    },
  });
}

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      reviewerNotes,
    }: {
      id: string;
      decision: ApprovalDecision;
      reviewerNotes: string;
    }) => api.approvals.decide(id, { decision, reviewerNotes }),
    onSuccess: () => {
      // Prefix-invalidate: refreshes every queue filter AND the open detail.
      qc.invalidateQueries({ queryKey: APPROVALS_KEY });
    },
  });
}

// -- Entity review: provenance, candidates, fusion (NER-05/06, HITL-01/02/03/05) --

const ENTITY_REVIEW_KEY = ["entity-review"] as const;

/** Every source that mentioned this entity (HITL-05). */
export function useEntityProvenance(entityId: string | undefined) {
  return useQuery({
    queryKey: [...ENTITY_REVIEW_KEY, "provenance", entityId],
    enabled: Boolean(entityId),
    queryFn: async () => (await api.entityReview.provenance(entityId!)).provenance,
  });
}

/** Scored duplicate candidates, with the reasoning behind each score (NER-05). */
export function useResolutionCandidates(entityId: string | undefined) {
  return useQuery({
    queryKey: [...ENTITY_REVIEW_KEY, "candidates", entityId],
    enabled: Boolean(entityId),
    queryFn: async () => (await api.entityReview.candidates(entityId!)).candidates,
  });
}

/**
 * What fusion would produce. Only fetched once a candidate is selected, because
 * it is the question "what happens if I agree" and asking it early is noise.
 */
export function useFusionPreview(entityId: string | undefined, canonicalId: string | undefined) {
  return useQuery({
    queryKey: [...ENTITY_REVIEW_KEY, "fusion-preview", entityId, canonicalId],
    enabled: Boolean(entityId && canonicalId),
    queryFn: async () => await api.entityReview.fusionPreview(entityId!, canonicalId!),
  });
}

/**
 * Confirm or reject. Invalidates the entity list so a decided item leaves the
 * queue immediately — the queue should only ever show what still needs a human.
 */
export function useReviewEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { entityId: string; status: "confirmed" | "rejected"; reason: string }) =>
      api.entityReview.review(v.entityId, { status: v.status, reason: v.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ENTITY_REVIEW_KEY });
    },
  });
}

/**
 * Decide many entities at once (HITL-02).
 *
 * Partial success is the normal case, not an error: the endpoint returns which
 * ids failed and why, and the rest are already committed. Rejecting the whole
 * batch because one id went stale would make bulk review unusable exactly when
 * it matters — a long queue that someone else is also working through.
 */
const PENDING_WARRANTS_KEY = ["warrants", "pending"] as const;

/** The supervisor queue. */
export function usePendingWarrants() {
  return useQuery({
    queryKey: PENDING_WARRANTS_KEY,
    queryFn: () => api.entityReview.warrants.pending(),
  });
}

/**
 * Sign or reject a submitted warrant.
 *
 * The API refuses a self-approval, so a rejection here is a real answer, not a
 * transport failure — surface the message rather than a generic error.
 */
export function useDecideWarrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { warrantId: string; status: "signed" | "rejected"; reason: string }) =>
      api.entityReview.warrants.decide(v.warrantId, { status: v.status, reason: v.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PENDING_WARRANTS_KEY });
      void qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

/** Create a draft warrant on a case. */
export function useCreateWarrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      caseId: string;
      warrantNumber: string;
      type: string;
      target: string;
      court?: string;
      calecpaPredicate?: string;
    }) => api.entityReview.warrants.create(v.caseId, v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["cases"] }),
  });
}

/** Machine-draft the probable-cause narrative onto a DRAFT warrant. */
export function useDraftAffidavit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { warrantId: string }) =>
      api.entityReview.warrants.draftAffidavit(v.warrantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["cases"] }),
  });
}

/** draft → pending_hitl. */
export function useSubmitWarrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { warrantId: string }) => api.entityReview.warrants.submit(v.warrantId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PENDING_WARRANTS_KEY });
      void qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useReviewEntitiesBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      entityIds: readonly string[];
      status: "confirmed" | "rejected";
      reason: string;
    }) => api.entityReview.reviewBatch({ ...v, entityIds: [...v.entityIds] }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ENTITY_REVIEW_KEY });
    },
  });
}

export function useFuseEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { entityId: string; canonicalId: string; reason: string }) =>
      api.entityReview.fuse(v.entityId, { canonicalId: v.canonicalId, reason: v.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ENTITY_REVIEW_KEY });
    },
  });
}

export function useUnfuseEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { entityId: string; reason: string }) =>
      api.entityReview.unfuse(v.entityId, { reason: v.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ENTITY_REVIEW_KEY });
    },
  });
}
